import { useEffect } from 'react';

/**
 * Runtime enhancer that gives every native `<select>` inside the admin
 * dashboard a searchable, Select2-style dropdown — without touching
 * any source `<select>` markup. The original element stays in the DOM
 * (hidden) so forms, validation, and `change` events continue to work.
 *
 * To opt out, add `data-no-enhance` to a specific `<select>`.
 */
export function SelectSearchEnhancer() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const SCOPE_SELECTOR = '.admin-content';
    let popover: HTMLElement | null = null;
    let activeSelect: HTMLSelectElement | null = null;
    let activeLabel: HTMLElement | null = null;
    let activeButton: HTMLButtonElement | null = null;
    let outsideHandler: ((e: MouseEvent) => void) | null = null;
    let keyHandler: ((e: KeyboardEvent) => void) | null = null;
    let rafId: number | null = null;
    let lastBtnRect: { top: number; left: number; width: number; bottom: number } | null = null;

    const close = () => {
      if (popover) {
        popover.remove();
        popover = null;
      }
      if (outsideHandler) document.removeEventListener('mousedown', outsideHandler, true);
      if (keyHandler) document.removeEventListener('keydown', keyHandler, true);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      outsideHandler = null;
      keyHandler = null;
      activeSelect = null;
      activeLabel = null;
      activeButton = null;
      lastBtnRect = null;
    };

    const trackLoop = () => {
      if (!popover || !activeButton) return;
      const r = activeButton.getBoundingClientRect();
      if (
        !lastBtnRect ||
        Math.abs(r.top - lastBtnRect.top) > 0.5 ||
        Math.abs(r.left - lastBtnRect.left) > 0.5 ||
        Math.abs(r.width - lastBtnRect.width) > 0.5
      ) {
        lastBtnRect = { top: r.top, left: r.left, width: r.width, bottom: r.bottom };
        reposition();
      }
      rafId = requestAnimationFrame(trackLoop);
    };

    const reposition = () => {
      if (!popover || !activeButton) return;
      // If the trigger is no longer visible, close.
      const rect = activeButton.getBoundingClientRect();
      if (
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        rect.right < 0 ||
        rect.left > window.innerWidth
      ) {
        close();
        return;
      }
      popover.style.top = `${rect.bottom + 4}px`;
      popover.style.left = `${rect.left}px`;
      popover.style.width = `${Math.max(rect.width, 220)}px`;
      const popRect = popover.getBoundingClientRect();
      if (popRect.right > window.innerWidth - 8) {
        popover.style.left = `${Math.max(8, window.innerWidth - popRect.width - 8)}px`;
      }
      if (popRect.bottom > window.innerHeight - 8) {
        popover.style.top = `${Math.max(8, rect.top - popRect.height - 4)}px`;
      }
    };

    const open = (select: HTMLSelectElement, button: HTMLButtonElement, label: HTMLElement) => {
      close();
      activeSelect = select;
      activeLabel = label;
      activeButton = button;

      const rect = button.getBoundingClientRect();
      const dir = getComputedStyle(document.documentElement).direction || 'ltr';

      popover = document.createElement('div');
      popover.className =
        'pos-select-enhanced-popover z-50 fixed rounded-md border border-input bg-popover text-popover-foreground shadow-md';
      popover.style.position = 'fixed';
      popover.style.top = `${rect.bottom + 4}px`;
      popover.style.left = `${rect.left}px`;
      popover.style.width = `${Math.max(rect.width, 220)}px`;
      popover.style.maxHeight = '320px';
      popover.style.display = 'flex';
      popover.style.flexDirection = 'column';
      popover.dir = dir;

      const searchWrap = document.createElement('div');
      searchWrap.className = 'border-b border-border p-2';
      const search = document.createElement('input');
      search.type = 'text';
      search.placeholder = dir === 'rtl' ? 'بحث...' : 'Search…';
      search.className =
        'flex h-9 w-full rounded-md border-0 bg-transparent px-3 py-1 text-sm text-popover-foreground outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0';
      searchWrap.appendChild(search);
      popover.appendChild(searchWrap);

      const list = document.createElement('div');
      list.className = 'overflow-y-auto py-1';
      list.style.flex = '1';
      popover.appendChild(list);

      const allOptions = Array.from(select.options).map((opt) => ({
        value: opt.value,
        text: opt.text,
        disabled: opt.disabled,
      }));

      const render = (filter: string) => {
        list.innerHTML = '';
        const needle = filter.trim().toLowerCase();
        const filtered = needle
          ? allOptions.filter((o) => o.text.toLowerCase().includes(needle))
          : allOptions;
        if (filtered.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'px-3 py-2 text-xs text-muted-foreground';
          empty.textContent = dir === 'rtl' ? 'لا توجد نتائج' : 'No results';
          list.appendChild(empty);
          return;
        }
        filtered.forEach((opt) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.dataset.value = opt.value;
          item.className =
            'flex w-full items-center justify-between px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50';
          item.style.textAlign = dir === 'rtl' ? 'right' : 'left';
          item.disabled = opt.disabled;
          const txt = document.createElement('span');
          txt.textContent = opt.text;
          item.appendChild(txt);
          if (opt.value === select.value) {
            const dot = document.createElement('span');
            dot.className = 'ms-2 inline-block h-1.5 w-1.5 rounded-full bg-primary';
            item.appendChild(dot);
          }
          item.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (opt.disabled) return;
            select.value = opt.value;
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
            if (activeLabel) activeLabel.textContent = opt.text;
            close();
          });
          list.appendChild(item);
        });
      };

      render('');
      search.addEventListener('input', () => render(search.value));

      document.body.appendChild(popover);
      // Keep within viewport horizontally
      const popRect = popover.getBoundingClientRect();
      if (popRect.right > window.innerWidth - 8) {
        popover.style.left = `${Math.max(8, window.innerWidth - popRect.width - 8)}px`;
      }
      // If overflow below, flip up
      if (popRect.bottom > window.innerHeight - 8) {
        popover.style.top = `${Math.max(8, rect.top - popRect.height - 4)}px`;
      }

      setTimeout(() => search.focus(), 0);

      outsideHandler = (e: MouseEvent) => {
        if (!popover) return;
        if (popover.contains(e.target as Node)) return;
        if (button.contains(e.target as Node)) return;
        close();
      };
      keyHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
      };
      document.addEventListener('mousedown', outsideHandler, true);
      document.addEventListener('keydown', keyHandler, true);

      // Track the trigger's position so the popover follows it on scroll,
      // resize, layout shifts, etc. — without relying on bubbling scroll events.
      lastBtnRect = { top: rect.top, left: rect.left, width: rect.width, bottom: rect.bottom };
      rafId = requestAnimationFrame(trackLoop);
    };

    const syncLabel = (select: HTMLSelectElement, label: HTMLElement) => {
      const sel = select.options[select.selectedIndex];
      label.textContent = sel?.text ?? '';
    };

    const enhance = (select: HTMLSelectElement) => {
      if (select.dataset.searchEnhanced === '1') return;
      if (select.hasAttribute('data-no-enhance')) return;
      if (select.multiple) return;
      // Skip selects whose parent is intentionally a fancy control
      if (select.closest('[data-no-enhance]')) return;
      select.dataset.searchEnhanced = '1';

      const wrap = document.createElement('span');
      wrap.style.position = 'relative';
      wrap.style.display = 'inline-block';
      wrap.style.width = '100%';
      wrap.className = 'pos-select-enhanced-wrap';

      const parent = select.parentNode;
      if (!parent) return;
      parent.insertBefore(wrap, select);
      wrap.appendChild(select);

      // Hide original visually, keep in form
      select.style.position = 'absolute';
      select.style.inset = '0';
      select.style.opacity = '0';
      select.style.pointerEvents = 'none';
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = select.className;
      btn.style.display = 'flex';
      btn.style.alignItems = 'center';
      btn.style.justifyContent = 'space-between';
      btn.style.gap = '0.5rem';
      btn.style.width = '100%';
      btn.style.cursor = 'pointer';
      btn.style.textAlign = 'start';
      // Reset some <select> arrow appearance leakage if any
      btn.style.appearance = 'none';

      const label = document.createElement('span');
      label.style.flex = '1';
      label.style.minWidth = '0';
      label.style.overflow = 'hidden';
      label.style.textOverflow = 'ellipsis';
      label.style.whiteSpace = 'nowrap';
      syncLabel(select, label);
      btn.appendChild(label);

      const caret = document.createElement('span');
      caret.setAttribute('aria-hidden', 'true');
      caret.style.opacity = '0.6';
      caret.style.fontSize = '0.75rem';
      caret.textContent = '▾';
      btn.appendChild(caret);

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (activeSelect === select) {
          close();
        } else {
          open(select, btn, label);
        }
      });

      // Keep button label in sync if external code changes the value
      const externalSync = () => syncLabel(select, label);
      select.addEventListener('change', externalSync);
      const obs = new MutationObserver(externalSync);
      obs.observe(select, { attributes: true, attributeFilter: ['value'], childList: true, subtree: true });

      wrap.appendChild(btn);
    };

    const scanAll = (root: ParentNode) => {
      root.querySelectorAll<HTMLSelectElement>('select').forEach(enhance);
    };

    const ensureScope = () => {
      const scope = document.querySelector(SCOPE_SELECTOR);
      if (!scope) return null;
      scanAll(scope);
      return scope;
    };

    let scope = ensureScope();
    // The .admin-content node may mount after this effect runs once.
    if (!scope) {
      const tries = setInterval(() => {
        scope = ensureScope();
        if (scope) clearInterval(tries);
      }, 100);
      setTimeout(() => clearInterval(tries), 5000);
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = n as HTMLElement;
          if (!el.closest?.(SCOPE_SELECTOR)) return;
          if (el.tagName === 'SELECT') enhance(el as HTMLSelectElement);
          el.querySelectorAll?.<HTMLSelectElement>('select').forEach(enhance);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const onScroll = (e: Event) => {
      // Ignore scroll happening inside our own popover (list scrolling)
      if (popover && e.target instanceof Node && popover.contains(e.target)) return;
      // Reposition the popover so it follows its trigger when the page scrolls
      reposition();
    };
    const onResize = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize, true);
      close();
    };
  }, []);

  return null;
}
